import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, CATEGORIES } from "../lib/api";
import EventCard from "../components/EventCard";
import { Input } from "../components/ui/input";
import { Search } from "lucide-react";

export default function Events() {
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "All";
  const q = params.get("q") || "";

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (category !== "All") query.category = category;
    if (q) query.q = q;
    api.get("/events", { params: query })
      .then(({ data }) => setEvents(data))
      .finally(() => setLoading(false));
  }, [category, q]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (!val || val === "All") next.delete(key); else next.set(key, val);
    setParams(next, { replace: true });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Discover</div>
        <h1 className="font-display text-3xl font-black uppercase text-zinc-50 sm:text-4xl">All events</h1>
      </div>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="events-search-input"
            defaultValue={q}
            onKeyDown={(e) => { if (e.key === "Enter") setParam("q", e.currentTarget.value); }}
            placeholder="Search title, venue, or city..."
            className="h-11 rounded-full border-zinc-800 bg-[#3A1020] pl-11 text-zinc-100 placeholder:text-zinc-500"
          />
        </div>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {["All", ...CATEGORIES].map((c) => (
          <button
            key={c}
            data-testid={`filter-category-${c.toLowerCase()}`}
            onClick={() => setParam("category", c)}
            className={`rounded-full border px-4 py-1.5 text-xs font-mono uppercase tracking-widest transition ${
              category === c
                ? "border-amber-500 bg-amber-500 text-zinc-950"
                : "border-zinc-800 bg-[#3A1020] text-zinc-300 hover:border-amber-500/50 hover:text-amber-400"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl bg-zinc-900" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-zinc-800 bg-[#3A1020] p-16 text-center">
          <div className="font-display text-xl uppercase text-zinc-200">No events found</div>
          <div className="mt-2 text-sm text-zinc-500">Try a different category or search.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {events.map((e) => <EventCard key={e.id} event={e} />)}
        </div>
      )}
    </div>
  );
}
